import { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { generateTheme, DEFAULT_PRIMARY_COLOR } from '../theme';
import { useAuth } from './AuthContext';

interface ThemeContextType {
  mode: 'light' | 'dark';
  primaryColor: string;
  toggleMode: () => void;
  setPrimaryColor: (color: string) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user, updateProfile } = useAuth();

  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('theme-mode');
    return (saved as 'light' | 'dark') || 'light';
  });

  const [primaryColor, setPrimaryColorState] = useState(() => {
    const saved = localStorage.getItem('theme-primary-color');
    return saved || DEFAULT_PRIMARY_COLOR;
  });

  useEffect(() => {
    if (user?.preferences) {
      setMode(user.preferences.theme);
      setPrimaryColorState(user.preferences.primaryColor);
    }
  }, [user?.preferences]);

  const toggleMode = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('theme-mode', newMode);
    if (user) {
      updateProfile({ preferences: { theme: newMode } }).catch(console.error);
    }
  };

  const setPrimaryColor = (color: string) => {
    setPrimaryColorState(color);
    localStorage.setItem('theme-primary-color', color);
    if (user) {
      updateProfile({ preferences: { primaryColor: color } }).catch(console.error);
    }
  };

  const theme = useMemo(() => generateTheme(primaryColor, mode), [primaryColor, mode]);

  return (
    <ThemeContext.Provider value={{ mode, primaryColor, toggleMode, setPrimaryColor }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
